import React, { useState, useEffect } from 'react';
import './BubbleInput.css';
import FloatingBubble from './FloatingBubble';
import TypingIndicator from './TypingIndicator';
import SuggestionBubbles from './SuggestionBubbles';
import aiService from '../services/aiService';
import { generateFineTuningData } from '../utils/fineTuningUtil';

function BubbleInput() {
  const [floatingBubbles, setFloatingBubbles] = useState([]);
  const [userName, setUserName] = useState('');
  const [currentStep, setCurrentStep] = useState('askName');
  const [inputValue, setInputValue] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [error, setError] = useState(null);

  // Add test URLs
  const TEST_URLS = [
    encodeURI('https://www.theschooloflife.com/shop/getting-over-your-parents'),
    encodeURI('https://www.theschooloflife.com/shop/what-they-forgot-to-teach-you-at-school'),
    encodeURI('https://www.theschooloflife.com/shop/the-confidence-workbook'),
    encodeURI('https://www.theschooloflife.com/shop/mood-map'),
    encodeURI('https://shop.theschooloflife.com/products/the-projects-goals-collection')
  ];

  // Add a delay utility function at the top
  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

  // Update test commands
  const handleTestCommands = async (input) => {
    switch(input.toLowerCase()) {
      case '/test knowledge':
        try {
          const data = await generateFineTuningData();
          console.log('Training Data:', data);
          return `Here are the resources I know about:\n- ${data.knowledge_base.map(k => k.title).join('\n- ')}`;
        } catch (error) {
          console.error('Error:', error);
          return "Error accessing knowledge base.";
        }
      
      case '/test confidence':
        try {
          const data = await generateFineTuningData();
          const confidenceContent = data.knowledge_base.find(k => 
            k.title.toLowerCase().includes('confidence')
          );
          return `Let me tell you about confidence: ${confidenceContent.content}\n\nKey points:\n${confidenceContent.key_points.map(p => `- ${p}`).join('\n')}`;
        } catch (error) {
          return "Error retrieving confidence information.";
        }

      case '/test persona':
        return "Hi Mike! I see you're interested in sports and cars. Given your interest in personal development, I think you might find The School of Life's approach particularly relevant. Would you like to explore some of their resources?";
      
      case '/test all':
        try {
          const data = await generateFineTuningData();
          return data.knowledge_base.map(k => 
            `${k.title}:\n${k.content.substring(0, 200)}...\n`
          ).join('\n\n');
        } catch (error) {
          return "Error retrieving content.";
        }

      default:
        return null;
    }
  };

  const handleKeyUp = (e) => {
    const bubbleId = Date.now();
    // Start from left side of screen for user bubbles
    const newBubble = {
      id: bubbleId,
      x: 50, // 50px from left edge
      y: window.innerHeight - 100, // Near bottom of screen
      isUser: true
    };

    setFloatingBubbles(prev => [...prev, newBubble]);

    setTimeout(() => {
      setFloatingBubbles(prev => prev.filter(bubble => bubble.id !== bubbleId));
    }, 2000);
  };

  // Update handleSubmit to include delays
  const handleSubmit = async (e) => {
    e.preventDefault();
    const userInput = inputValue.trim();
    
    if (!userInput) return;

    setInputValue('');
    setIsThinking(true);
    setError(null);

    try {
      if (currentStep === 'askName') {
        if (userInput.toLowerCase() === 'mike' || userInput.toLowerCase() === 'michael') {
          setUserName(userInput);
          setCurrentStep('conversation');
          // Add typing delay
          await delay(2000);
          setConversation(prev => [...prev, {
            user: userInput,
            assistant: `Mike! It's so wonderful to see you again! I've been thinking about your daily walks - how are they going? I remember we talked about how they help with clearing your mind. Keep up that great habit!`
          }]);
        } else {
          await delay(1500);
          setConversation(prev => [...prev, {
            user: userInput,
            assistant: "I'm sorry, I think there might be a mistake. I'm looking for my friend Mike. Is that you?"
          }]);
        }
      } else {
        // Add user message immediately
        setConversation(prev => [...prev, { user: userInput, assistant: null }]);
        
        // Add longer typing delay for main conversations
        await delay(3000);
        
        // Get AI response
        const response = await aiService.generateResponse(
          userInput,
          conversation.map(msg => [
            { role: 'user', content: msg.user },
            { role: 'assistant', content: msg.assistant }
          ]).flat()
        );

        // Update conversation with AI response
        setConversation(prev => {
          const updated = [...prev];
          updated[updated.length - 1].assistant = response;
          return updated;
        });
      }
    } catch (err) {
      setError('Sorry, I had trouble responding. Please try again.');
      console.error('Error:', err);
      setConversation(prev => prev.filter(msg => msg.assistant !== null));
    } finally {
      setIsThinking(false);
    }
  };

  const getBubbleMessage = () => {
    if (error) return error;
    
    switch (currentStep) {
      case 'askName':
        return "Hey! Nice to meet you! What's your name?";
      case 'conversation':
        if (conversation.length === 0) {
          return `Mike! It's so wonderful to see you again! I've been thinking about your daily walks - how are they going? I remember we talked about how they help with clearing your mind. Keep up that great habit!`;
        }
        return conversation[conversation.length - 1].assistant;
      default:
        return '';
    }
  };

  // Update the typing bubbles effect
  useEffect(() => {
    if (!isThinking) return;
    
    // Create more bubbles with varying positions
    const createBubble = () => {
      const bubbleId = Date.now() + Math.random();
      const randomOffset = Math.random() * 100 - 50; // Random offset between -50 and 50 pixels
      
      return {
        id: bubbleId,
        x: window.innerWidth - 100 + randomOffset, // Vary the horizontal position
        y: window.innerHeight - 150 + (Math.random() * 50), // Vary the vertical position
        isLunaTyping: true
      };
    };

    // Create bubbles more frequently
    const interval = setInterval(() => {
      setFloatingBubbles(prev => [...prev, createBubble()]);
    }, 200); // Create a new bubble every 200ms

    // Remove bubbles after varying durations
    const cleanup = setInterval(() => {
      setFloatingBubbles(prev => 
        prev.filter(bubble => Date.now() - bubble.id < 1500 + (Math.random() * 500))
      );
    }, 100);

    return () => {
      clearInterval(interval);
      clearInterval(cleanup);
    };
  }, [isThinking]);

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    // Optional: automatically submit the suggestion
    handleSubmit(new Event('submit'));
  };

  return (
    <div className="bubble-input-container">
      <div className="chat-history">
        {conversation.map((msg, index) => (
          <div key={index} className="chat-messages">
            <div className="user-message">
              <div className="message-bubble user">
                {msg.user}
              </div>
            </div>
            {msg.assistant && (
              <div className="luna-message">
                <div className="message-bubble luna">
                  {msg.assistant}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="bubble-container">
        <div className="bubble luna-bubble">
          {isThinking ? (
            <TypingIndicator />
          ) : (
            <span>{getBubbleMessage()}</span>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          <input 
            type="text" 
            className="input-field"
            placeholder={currentStep === 'askName' ? "Type your name here..." : "Type your thoughts here..."}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyUp={handleKeyUp}
          />
        </form>
      </div>
      {floatingBubbles.map(bubble => (
        <FloatingBubble
          key={bubble.id}
          initialX={bubble.x}
          initialY={bubble.y}
          isUser={!bubble.isLunaTyping}
          isLunaTyping={bubble.isLunaTyping}
        />
      ))}
      <SuggestionBubbles onSuggestionClick={handleSuggestionClick} />
    </div>
  );
}

export default BubbleInput; 